import React, { useEffect, useMemo, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  updateDoc
} from 'firebase/firestore';
import { Edit2, MapPin, Plus, Search, Trash2, Warehouse as WarehouseIcon, X } from 'lucide-react';
import Spinner from './Spinner.js';

const emptyWarehouseForm = {
  name: '',
  code: '',
  contactPerson: '',
  phone: '',
  email: '',
  address: '',
  city: '',
  state: '',
  pincode: '',
  status: 'Active'
};

const AdminWarehouses = ({ db }) => {
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [showModal, setShowModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [formData, setFormData] = useState(emptyWarehouseForm);

  useEffect(() => {
    const warehousesQuery = query(collection(db, 'warehouses'), orderBy('name', 'asc'));
    const unsubscribe = onSnapshot(warehousesQuery, (snapshot) => {
      const nextWarehouses = snapshot.docs.map((warehouseDoc) => ({
        id: warehouseDoc.id,
        ...warehouseDoc.data()
      }));
      setWarehouses(nextWarehouses);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db]);

  const filteredWarehouses = useMemo(() => (
    warehouses.filter((warehouse) => {
      const normalizedSearch = searchTerm.toLowerCase();
      const matchesSearch =
        warehouse.name?.toLowerCase().includes(normalizedSearch) ||
        warehouse.code?.toLowerCase().includes(normalizedSearch) ||
        warehouse.city?.toLowerCase().includes(normalizedSearch) ||
        warehouse.state?.toLowerCase().includes(normalizedSearch) ||
        warehouse.contactPerson?.toLowerCase().includes(normalizedSearch);
      const matchesStatus = statusFilter === 'All' || warehouse.status === statusFilter;
      return matchesSearch && matchesStatus;
    })
  ), [warehouses, searchTerm, statusFilter]);

  const handleOpenCreate = () => {
    setEditingWarehouse(null);
    setFormData(emptyWarehouseForm);
    setShowModal(true);
  };

  const handleOpenEdit = (warehouse) => {
    setEditingWarehouse(warehouse);
    setFormData({
      name: warehouse.name || '',
      code: warehouse.code || '',
      contactPerson: warehouse.contactPerson || '',
      phone: warehouse.phone || '',
      email: warehouse.email || '',
      address: warehouse.address || '',
      city: warehouse.city || '',
      state: warehouse.state || '',
      pincode: warehouse.pincode || '',
      status: warehouse.status || 'Active'
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    if (isSaving) return;
    setShowModal(false);
    setEditingWarehouse(null);
    setFormData(emptyWarehouseForm);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!String(formData.name || '').trim() || !String(formData.code || '').trim()) {
      alert('Warehouse name and code are required.');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        name: String(formData.name || '').trim(),
        code: String(formData.code || '').trim(),
        contactPerson: String(formData.contactPerson || '').trim(),
        phone: String(formData.phone || '').trim(),
        email: String(formData.email || '').trim(),
        address: String(formData.address || '').trim(),
        city: String(formData.city || '').trim(),
        state: String(formData.state || '').trim(),
        pincode: String(formData.pincode || '').trim(),
        status: formData.status || 'Active',
        updatedAt: new Date().toISOString()
      };

      if (editingWarehouse?.id) {
        await updateDoc(doc(db, 'warehouses', editingWarehouse.id), payload);
      } else {
        await addDoc(collection(db, 'warehouses'), {
          ...payload,
          createdAt: new Date().toISOString()
        });
      }

      handleCloseModal();
    } catch (error) {
      console.error('Error saving warehouse:', error);
      alert(`Error saving warehouse: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (warehouseId, warehouseName) => {
    if (!window.confirm(`Delete warehouse "${warehouseName}"? This action cannot be undone.`)) return;

    try {
      await deleteDoc(doc(db, 'warehouses', warehouseId));
    } catch (error) {
      console.error('Error deleting warehouse:', error);
      alert(`Error deleting warehouse: ${error.message}`);
    }
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: '20px' }}>
      <div style={headerStyle}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>Warehouse Master</h1>
          <p style={{ fontSize: '14px', color: '#64748b', margin: '5px 0 0 0' }}>
            Create, review, update and delete entries from the Firestore `warehouses` collection.
          </p>
        </div>
        <button onClick={handleOpenCreate} style={primaryButtonStyle}>
          <Plus size={18} /> Add Warehouse
        </button>
      </div>

      <div style={filterBarStyle}>
        <div style={{ position: 'relative', flex: 1, minWidth: '280px' }}>
          <Search size={18} style={searchIconStyle} />
          <input
            type="text"
            placeholder="Search by name, code, city or contact person..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            style={searchInputStyle}
          />
        </div>

        <select
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          style={selectStyle}
        >
          <option value="All">All Statuses</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </div>

      <div style={summaryGridStyle}>
        <SummaryCard label="Total Warehouses" value={warehouses.length} accent="#2563eb" />
        <SummaryCard label="Active" value={warehouses.filter((warehouse) => warehouse.status === 'Active').length} accent="#16a34a" />
        <SummaryCard label="Inactive" value={warehouses.filter((warehouse) => warehouse.status === 'Inactive').length} accent="#dc2626" />
      </div>

      <div style={tableContainerStyle}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={theadStyle}>
              <th style={thStyle}>Warehouse</th>
              <th style={thStyle}>Code</th>
              <th style={thStyle}>Location</th>
              <th style={thStyle}>Contact</th>
              <th style={thStyle}>Status</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredWarehouses.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  No warehouses match the current filters.
                </td>
              </tr>
            ) : (
              filteredWarehouses.map((warehouse) => (
                <tr key={warehouse.id} style={trStyle}>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={warehouseIconWrapStyle}>
                        <WarehouseIcon size={18} color="#2563eb" />
                      </div>
                      <div>
                        <div style={{ fontWeight: '700', color: '#0f172a' }}>{warehouse.name || 'Unnamed Warehouse'}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{warehouse.address || 'Address not added yet'}</div>
                      </div>
                    </div>
                  </td>
                  <td style={tdStyle}>
                    <span style={codeBadgeStyle}>{warehouse.code || '--'}</span>
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#475569' }}>
                      <MapPin size={14} />
                      <span>{[warehouse.city, warehouse.state].filter(Boolean).join(', ') || '--'}</span>
                    </div>
                    <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>{warehouse.pincode || ''}</div>
                  </td>
                  <td style={tdStyle}>
                    <div>{warehouse.contactPerson || '--'}</div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>{warehouse.phone || warehouse.email || '--'}</div>
                  </td>
                  <td style={tdStyle}>
                    <span style={warehouse.status === 'Active' ? activeBadgeStyle : inactiveBadgeStyle}>
                      {warehouse.status || 'Active'}
                    </span>
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                      <button onClick={() => handleOpenEdit(warehouse)} style={actionButtonStyle} title="Edit warehouse">
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(warehouse.id, warehouse.name || 'this warehouse')}
                        style={{ ...actionButtonStyle, color: '#dc2626' }}
                        title="Delete warehouse"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <WarehouseModal
          formData={formData}
          isSaving={isSaving}
          isEditing={Boolean(editingWarehouse)}
          onChange={handleChange}
          onClose={handleCloseModal}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

const SummaryCard = ({ label, value, accent }) => (
  <div style={{ ...summaryCardStyle, borderTop: `4px solid ${accent}` }}>
    <div style={{ fontSize: '13px', color: '#64748b' }}>{label}</div>
    <div style={{ fontSize: '28px', fontWeight: '700', color: '#0f172a', marginTop: '8px' }}>{value}</div>
  </div>
);

const WarehouseModal = ({ formData, isSaving, isEditing, onChange, onClose, onSave }) => (
  <div style={modalOverlayStyle}>
    <div style={modalContentStyle}>
      <div style={modalHeaderStyle}>
        <div>
          <h2 style={{ margin: 0 }}>{isEditing ? 'Edit Warehouse' : 'Add Warehouse'}</h2>
          <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '14px' }}>
            Capture the operational details used across dispatch, inward and inventory flows.
          </p>
        </div>
        <button onClick={onClose} style={iconButtonStyle} disabled={isSaving}>
          <X size={20} />
        </button>
      </div>

      <div style={modalBodyStyle}>
        <div style={rowStyle}>
          <FormField label="Warehouse Name *">
            <input name="name" value={formData.name} onChange={onChange} style={inputStyle} />
          </FormField>
          <FormField label="Warehouse Code *">
            <input name="code" value={formData.code} onChange={onChange} style={inputStyle} />
          </FormField>
        </div>

        <div style={rowStyle}>
          <FormField label="Contact Person">
            <input name="contactPerson" value={formData.contactPerson} onChange={onChange} style={inputStyle} />
          </FormField>
          <FormField label="Phone Number">
            <input name="phone" value={formData.phone} onChange={onChange} style={inputStyle} />
          </FormField>
        </div>

        <div style={rowStyle}>
          <FormField label="Email Address">
            <input name="email" value={formData.email} onChange={onChange} style={inputStyle} />
          </FormField>
          <FormField label="Status">
            <select name="status" value={formData.status} onChange={onChange} style={inputStyle}>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </FormField>
        </div>

        <FormField label="Address">
          <textarea name="address" value={formData.address} onChange={onChange} style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} />
        </FormField>

        <div style={rowStyle}>
          <FormField label="City">
            <input name="city" value={formData.city} onChange={onChange} style={inputStyle} />
          </FormField>
          <FormField label="State">
            <input name="state" value={formData.state} onChange={onChange} style={inputStyle} />
          </FormField>
          <FormField label="PIN Code">
            <input name="pincode" value={formData.pincode} onChange={onChange} style={inputStyle} />
          </FormField>
        </div>
      </div>

      <div style={modalFooterStyle}>
        <button onClick={onClose} style={secondaryButtonStyle} disabled={isSaving}>Cancel</button>
        <button onClick={onSave} style={primaryButtonStyle} disabled={isSaving}>
          {isSaving ? 'Saving...' : (isEditing ? 'Update Warehouse' : 'Save Warehouse')}
        </button>
      </div>
    </div>
  </div>
);

const FormField = ({ label, children }) => (
  <div style={{ flex: 1 }}>
    <label style={fieldLabelStyle}>{label}</label>
    {children}
  </div>
);

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '16px',
  marginBottom: '24px',
  flexWrap: 'wrap'
};

const filterBarStyle = {
  display: 'flex',
  gap: '15px',
  marginBottom: '20px',
  backgroundColor: 'white',
  padding: '15px',
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  flexWrap: 'wrap'
};

const summaryGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: '16px',
  marginBottom: '20px'
};

const summaryCardStyle = {
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '18px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  border: '1px solid #e2e8f0'
};

const primaryButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  padding: '10px 18px',
  borderRadius: '8px',
  border: 'none',
  backgroundColor: '#2563eb',
  color: 'white',
  fontWeight: '600',
  cursor: 'pointer'
};

const secondaryButtonStyle = {
  padding: '10px 18px',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  backgroundColor: 'white',
  color: '#334155',
  fontWeight: '600',
  cursor: 'pointer'
};

const searchIconStyle = {
  position: 'absolute',
  left: '12px',
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#94a3b8'
};

const searchInputStyle = {
  width: '100%',
  padding: '10px 12px 10px 40px',
  borderRadius: '8px',
  border: '1px solid #e2e8f0',
  fontSize: '14px',
  outline: 'none'
};

const selectStyle = {
  minWidth: '180px',
  padding: '10px 12px',
  borderRadius: '8px',
  border: '1px solid #e2e8f0',
  fontSize: '14px',
  outline: 'none',
  backgroundColor: 'white'
};

const tableContainerStyle = {
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  overflow: 'hidden',
  border: '1px solid #e2e8f0'
};

const theadStyle = {
  backgroundColor: '#f8fafc',
  borderBottom: '1px solid #e2e8f0'
};

const thStyle = {
  padding: '14px 18px',
  textAlign: 'left',
  fontSize: '11px',
  fontWeight: '700',
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.05em'
};

const tdStyle = {
  padding: '16px 18px',
  fontSize: '14px',
  verticalAlign: 'middle'
};

const trStyle = {
  borderBottom: '1px solid #f1f5f9'
};

const warehouseIconWrapStyle = {
  width: '36px',
  height: '36px',
  borderRadius: '10px',
  backgroundColor: '#eff6ff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
};

const codeBadgeStyle = {
  display: 'inline-flex',
  padding: '4px 8px',
  borderRadius: '999px',
  backgroundColor: '#eff6ff',
  color: '#1d4ed8',
  fontWeight: '700',
  fontSize: '12px'
};

const activeBadgeStyle = {
  display: 'inline-flex',
  padding: '4px 10px',
  borderRadius: '999px',
  backgroundColor: '#dcfce7',
  color: '#166534',
  fontSize: '12px',
  fontWeight: '700'
};

const inactiveBadgeStyle = {
  display: 'inline-flex',
  padding: '4px 10px',
  borderRadius: '999px',
  backgroundColor: '#fee2e2',
  color: '#991b1b',
  fontSize: '12px',
  fontWeight: '700'
};

const actionButtonStyle = {
  width: '34px',
  height: '34px',
  borderRadius: '8px',
  border: '1px solid #e2e8f0',
  backgroundColor: 'white',
  color: '#475569',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const modalOverlayStyle = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(15, 23, 42, 0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
  zIndex: 3000
};

const modalContentStyle = {
  width: '760px',
  maxWidth: '100%',
  maxHeight: '90vh',
  overflow: 'hidden',
  backgroundColor: 'white',
  borderRadius: '16px',
  boxShadow: '0 20px 40px rgba(15, 23, 42, 0.22)',
  display: 'flex',
  flexDirection: 'column'
};

const modalHeaderStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '16px',
  padding: '22px 24px',
  borderBottom: '1px solid #e2e8f0'
};

const modalBodyStyle = {
  padding: '24px',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px'
};

const modalFooterStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '10px',
  padding: '18px 24px',
  borderTop: '1px solid #e2e8f0'
};

const rowStyle = {
  display: 'flex',
  gap: '16px',
  flexWrap: 'wrap'
};

const fieldLabelStyle = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '11px',
  fontWeight: '700',
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.04em'
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box'
};

const iconButtonStyle = {
  width: '36px',
  height: '36px',
  borderRadius: '10px',
  border: '1px solid #e2e8f0',
  backgroundColor: 'white',
  color: '#475569',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center'
};

export default AdminWarehouses;
